import React, { useState, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import { cn } from "@/lib/utils";
import PlatformConnection from "@/components/connections/PlatformConnection";
import EmployeeList from "../EmployeeList";
import EmployeeScoresList from "../EmployeeScoresList";
import IngestionStatusBanner from "../IngestionStatusBanner";
import ScoringSetup from "../ScoringSetup";
import { useOnboardingSteps, STEPS, StepId } from "./useOnboardingSteps";
import { workspacesApi } from "@/api/workspaces";
import { employeesApi } from "@/api/employees";
import type { IngestionStatus } from "../../hooks/useIngestionStatus";

interface OnboardingProps {
  onComplete: () => void;
}

const Onboarding: React.FunctionComponent<OnboardingProps> = ({
  onComplete,
}) => {
  const [supportConnected, setSupportConnected] = useState(false);
  const [messagingConnected, setMessagingConnected] = useState(false);
  const [ingestionStatus, setIngestionStatus] = useState<IngestionStatus>("not_started");

  const handleIngestionStatusChange = useCallback((status: IngestionStatus) => {
    setIngestionStatus(status);
  }, []);

  const {
    currentStep,
    currentStepData,
    currentStepId,
    completedSteps,
    handleBack,
    handleTabChange,
    isStepDisabled,
    isContinueDisabled,
    buttonText,
    markCurrentCompleteAndAdvance,
  } = useOnboardingSteps({ supportConnected, messagingConnected, saving: false });

  const stepHasInternalSave =
    currentStepId === StepId.AGENTS || currentStepId === StepId.SCORING;

  const handleSaveAndContinue = () => {
    if (currentStepId === StepId.SUPPORT) {
      employeesApi.sync();
    }
    if (currentStepId === StepId.MESSAGING) {
      workspacesApi.triggerInitialPull();
    }
    markCurrentCompleteAndAdvance(onComplete);
  };

  return (
    <div className="mb-8 max-w-6xl">
      <div className="mb-6">
        <Heading level={1} variant="lg">Set up your workspace</Heading>
        <Text variant="sm" className="text-muted-foreground">
          Complete these steps to get the most out of Punch Cards
        </Text>
      </div>
      <Tabs
          value={currentStepData.id}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList className="w-full grid grid-cols-5 h-auto p-1">
            {STEPS.map((step, index) => {
              const isCompleted = completedSteps.has(index);
              const isCurrent = index === currentStep;
              const isDisabled = isStepDisabled(index);

              return (
                <TabsTrigger
                  key={step.id}
                  value={step.id}
                  disabled={isDisabled}
                  className={cn(
                    "flex flex-col gap-1 py-3 px-2 h-auto",
                    isCompleted &&
                      !isCurrent &&
                      "text-primary data-[state=active]:text-primary",
                    isDisabled && "opacity-40 cursor-not-allowed"
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className={cn(
                        "flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold border-2",
                        isCurrent &&
                          "border-primary bg-primary text-primary-foreground",
                        isCompleted &&
                          !isCurrent &&
                          "border-primary bg-primary/10 text-primary",
                        !isCurrent &&
                          !isCompleted &&
                          "border-muted-foreground/30 text-muted-foreground"
                      )}
                    >
                      {isCompleted && !isCurrent ? "✓" : index + 1}
                    </span>
                    <span className="hidden lg:inline text-sm font-medium truncate">
                      {step.tab}
                    </span>
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="support" className="mt-6">
            <div className="space-y-4">
              <div>
                <Heading level={2} variant="sm">{STEPS[0].title}</Heading>
                <Text className="text-muted-foreground mt-1">{STEPS[0].description}</Text>
              </div>
              <PlatformConnection
                platform="intercom"
                onConnectionChange={setSupportConnected}
              />
            </div>
          </TabsContent>

          <TabsContent value="messaging" className="mt-6">
            <div className="space-y-4">
              <div>
                <Heading level={2} variant="sm">{STEPS[1].title}</Heading>
                <Text className="text-muted-foreground mt-1">{STEPS[1].description}</Text>
              </div>
              <PlatformConnection
                platform="slack"
                onConnectionChange={setMessagingConnected}
              />
            </div>
          </TabsContent>

          <TabsContent value="agents" className="mt-6">
            <div className="space-y-4">
              <div>
                <Heading level={2} variant="sm">{STEPS[2].title}</Heading>
                <Text className="text-muted-foreground mt-1">
                  {STEPS[2].description}
                </Text>
              </div>
              <EmployeeList hideRefresh onSave={handleSaveAndContinue} onBack={handleBack} saveLabel="Save and Continue" />
            </div>
          </TabsContent>

          <TabsContent value="scoring" className="mt-6">
            <ScoringSetup onSave={handleSaveAndContinue} onBack={handleBack} saveLabel="Save and Continue" prefillDefaults />
          </TabsContent>

          <TabsContent value="review" className="mt-6">
            <div className="space-y-4">
              <div>
                <Heading level={2} variant="sm">{STEPS[4].title}</Heading>
                <Text className="text-muted-foreground mt-1">
                  {STEPS[4].description}
                </Text>
              </div>
              <IngestionStatusBanner onStatusChange={handleIngestionStatusChange} />
              {ingestionStatus !== "failed" && <EmployeeScoresList preview />}
            </div>
          </TabsContent>
        </Tabs>
      {!stepHasInternalSave && (
        <div className="flex justify-between gap-3 mt-6">
          {currentStep > 0 ? (
            <Button variant="outline" onClick={handleBack}>
              Back
            </Button>
          ) : (
            <div />
          )}
          <Button
            onClick={handleSaveAndContinue}
            disabled={isContinueDisabled || (currentStepId === StepId.REVIEW && ingestionStatus !== "completed")}
          >
            {buttonText}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Onboarding;
